import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";
import type { components } from "@/lib/api/schema.gen";
import { unwrap } from "@/lib/api/unwrap";
import { queryKeys } from "@/lib/query/keys";

/**
 * Billing data layer — typed openapi-fetch calls wrapped in TanStack Query.
 *
 * Query keys are kept in a local namespace (rather than the central factory) so
 * this vertical slice stays self-contained; mutations invalidate the
 * subscription-status subtree so the summary reflects the new state.
 */

export type BillingInformation = components["schemas"]["BillingInformation"];
export type PatchedBillingInformation =
  components["schemas"]["PatchedBillingInformation"];
export type StripeProduct = components["schemas"]["StripeProduct"];
export type SubscriptionStatus = components["schemas"]["SubscriptionStatus"];
export type SubscriptionCreate = components["schemas"]["SubscriptionCreate"];
export type PromoValidationResponse =
  components["schemas"]["PromoValidationResponse"];
export type SetupIntent = components["schemas"]["SetupIntent"];

export const billingKeys = {
  all: ["billing"] as const,
  info: () => [...billingKeys.all, "information"] as const,
  products: () => [...billingKeys.all, "products"] as const,
  status: () => [...billingKeys.all, "subscription", "status"] as const,
};

export function useBillingInformation() {
  return useQuery({
    queryKey: billingKeys.info(),
    queryFn: () => unwrap(api.GET("/api/v1/billing/information/")),
  });
}

/**
 * Persist a partial billing-information change. Honours the AbortSignal so the
 * autosave hook can cancel a superseded in-flight save (last-write-wins).
 */
export function saveBillingInformation(
  body: PatchedBillingInformation,
  signal: AbortSignal,
): Promise<BillingInformation> {
  return unwrap(
    api.PATCH("/api/v1/billing/information/", { body, signal }),
  );
}

export function useStripeProducts() {
  return useQuery({
    queryKey: billingKeys.products(),
    queryFn: () => unwrap(api.GET("/api/v1/billing/products/")),
  });
}

export function useSubscriptionStatus() {
  return useQuery({
    queryKey: billingKeys.status(),
    queryFn: () => unwrap(api.GET("/api/v1/billing/subscription/status/")),
  });
}

export function useCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SubscriptionCreate) =>
      unwrap(api.POST("/api/v1/billing/subscription/create/", { body })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.status() });
      // A partner/promo code joins a group, which flips the server-computed
      // `permissions.is_group_member` the whole app reads — refetch the current
      // user, or the UI keeps rendering them as a non-member.
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
      void queryClient.invalidateQueries({ queryKey: ["partners"] });
    },
  });
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      unwrap(api.POST("/api/v1/billing/subscription/cancel/")),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: billingKeys.status() }),
  });
}

/**
 * Validate the single code field: a group's partner code, a promo code bound to
 * a group, or a plain Stripe promotion code. When it belongs to a group the
 * response also carries `group_name`, so we can name the group being joined.
 */
export function useValidateCode() {
  return useMutation({
    mutationFn: (code: string) =>
      unwrap(
        api.POST("/api/v1/billing/promo/validate/", {
          body: { code },
        }),
      ),
  });
}

export function useSetupIntent() {
  return useMutation({
    mutationFn: () => unwrap(api.POST("/api/v1/billing/setup-intent/")),
  });
}

/**
 * Finalize a just-added card: make it the default and detach any older cards so
 * only one remains on file (F-b — the SetupIntent/confirmSetup flow attaches the
 * new card but never detaches the old one). Best-effort; called after
 * confirmSetup succeeds.
 */
export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (paymentMethodId: string) =>
      unwrap(
        api.POST("/api/v1/billing/payment-method/", {
          body: { payment_method_id: paymentMethodId },
        }),
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: billingKeys.status() }),
  });
}
